import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import {
  HiOutlineStar, HiOutlineTruck, HiCheck, HiArrowRight, HiArrowLeft,
  HiLockClosed, HiInformationCircle, HiCalendarDays, HiXMark
} from 'react-icons/hi2'
import { IoMdClose } from 'react-icons/io'
import { FaTruck } from 'react-icons/fa'
import { toast } from 'react-toastify'
import Axios from '../../Axios'
import { Urls } from '../../Urls'
import styles from './RealiseSavings.module.css'
import { UserApiContext } from '../UserDetailsContext/UserDetailsContext'
import { IS_DEV, LORRI_NUMBERS } from '../../Constants'

// ── Feature flag & config ──
const ENABLE_RAZORPAY_PAYMENT = true
const DEFAULT_PAYMENT_AMOUNT = 1000
const RAZORPAY_KEY = IS_DEV ? 'rzp_test_SvC3V7WIElhuQ3' : 'rzp_live_SvC3V7WIElhuQ3'

const SUBSCRIBE_BULLETS = [
  { bold: 'Price discovery', rest: ' — know your true market rate. Negotiate from data, not guesswork.' },
  { bold: 'Transporter profiles', rest: ' — 35+ data points per vendor — fleet, ratings, financials, lane history — to pick the right partner.' },
]

const RFQ_BULLETS = [
  { bold: 'Pooled volume, better rates', rest: ' — combine your lanes with other shippers into one large RFQ. Bigger, committed volume is more efficient for carriers to serve.' },
  { bold: 'Better economics for all', rest: ' — that efficiency comes back to you as sharper rates and better service.' },
]

export default function RealiseSavings({ onBack, lanesCount, remainingLanes, source }) {
  const history = useHistory()
  const creditsExhausted = remainingLanes === 0
  const { userDetails } = useContext(UserApiContext)

  // ── Razorpay / token amount modal ──
  const [showAmountModal, setShowAmountModal] = useState(false)
  const [tokenAmount, setTokenAmount] = useState(DEFAULT_PAYMENT_AMOUNT)

  // ── Floating widget ──
  const [showWidget, setShowWidget] = useState(false)
  const [widgetDismissed, setWidgetDismissed] = useState(
    () => sessionStorage.getItem('statsWidgetDismissed') === 'true'
  )

  // ── Express Interest modal ──
  const [showExpressModal, setShowExpressModal] = useState(false)
  const [procurementWindow, setProcurementWindow] = useState('')
  const [laneCount, setLaneCount] = useState('')
  const [notes, setNotes] = useState('')
  const [submittingForSelf, setSubmittingForSelf] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [submittingInterest, setSubmittingInterest] = useState(false)

  // Load Razorpay script
  useEffect(() => {
    if (!ENABLE_RAZORPAY_PAYMENT) return
    if (document.getElementById('razorpay-checkout-script')) return
    const script = document.createElement('script')
    script.id = 'razorpay-checkout-script'
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    document.body.appendChild(script)
  }, [])

  // Floating widget delay
  useEffect(() => {
    if (widgetDismissed) return
    const timer = setTimeout(() => setShowWidget(true), 3000)
    return () => clearTimeout(timer)
  }, [widgetDismissed])

  // ── Razorpay handlers ──
  const handleSubscribeSuccess = (paymentId) => {
    const url = paymentId
      ? `${Urls.benchmarnow}?field=log_subscription&payment_id=${paymentId}`
      : `${Urls.benchmarnow}?field=log_subscription`

    Axios.post(url, {})
      .then(res => {
        if (res.data?.status) {
          toast.success('Subscribed! Confirmation sent to your email.')
        } else {
          toast.error('Subscription recorded, but there was an issue. Please contact support.')
        }
      })
      .catch(() => {
        toast.error('Something went wrong. Please contact support.')
      })
  }

  const openRazorpay = (amount) => {
    const options = {
      key: RAZORPAY_KEY,
      amount: amount * 100,
      currency: 'INR',
      name: 'LoRRI Benchmark',
      description: 'LoRRI Benchmark Subscription',
      prefill: {
        name: userDetails?.display_name || localStorage.getItem('userdetails') || '',
        email: userDetails?.email || localStorage.getItem('useremail') || '',
      },
      theme: { color: '#393185' },
      handler: (response) => {
        setShowAmountModal(false)
        handleSubscribeSuccess(response.razorpay_payment_id)
      },
    }
    const rzp = new window.Razorpay(options)
    rzp.open()
  }

  const handleSubscribeClick = () => {
    if (ENABLE_RAZORPAY_PAYMENT) {
      setTokenAmount(DEFAULT_PAYMENT_AMOUNT)
      setShowAmountModal(true)
    } else {
      handleSubscribeSuccess(null)
    }
  }

  const handleAmountPay = () => {
    const amt = Number(tokenAmount)
    if (!amt || amt < 1) {
      toast.error('Please enter a valid amount (minimum ₹1)')
      return
    }
    openRazorpay(amt)
  }

  // ── Express Interest handlers ──
  const handleExpressSubmit = () => {
    let windowStr = ''
    if (procurementWindow === 'monsoon') {
      windowStr = 'July / August'
    } else if (procurementWindow === 'annual') {
      windowStr = 'December / January'
    } else if (procurementWindow === 'other') {
      windowStr = 'Other / Not sure yet'
    }

    const orgName = userDetails?.company_name || localStorage.getItem('companyname') || ''

    const payload = {
      procurement_window: windowStr,
      lane_count: laneCount ? String(laneCount) : '',
      organisation: orgName,
      notes: notes || ''
    }

    setSubmittingInterest(true)
    const url = `${Urls.benchmarnow}?field=express_interest`

    Axios.post(url, payload)
      .then(res => {
        if (res.data?.status) {
          toast.success(res.data?.message || 'Interest recorded successfully.')
          setSubmitted(true)
        } else {
          toast.error(res.data?.message || 'Failed to submit interest. Please try again.')
        }
      })
      .catch(err => {
        console.error('Express interest error:', err)
        toast.error(err.response?.data?.message || 'Something went wrong. Please contact support.')
      })
      .finally(() => {
        setSubmittingInterest(false)
      })
  }

  const handleCloseExpressModal = () => {
    setShowExpressModal(false)
    setSubmitted(false)
    setProcurementWindow('')
    setLaneCount('')
    setNotes('')
    setSubmittingForSelf(true)
    setSubmittingInterest(false)
  }

  const handleDismissWidget = () => {
    setShowWidget(false)
    setWidgetDismissed(true)
    sessionStorage.setItem('statsWidgetDismissed', 'true')
  }

  const buildScheduleFormUrl = () => {
    const params = new URLSearchParams()
    if (userDetails?.display_name) params.set('name', userDetails.display_name)
    if (userDetails?.email) params.set('email', userDetails.email)
    if (userDetails?.company_name) params.set('company_name', userDetails.company_name)
    const qs = params.toString()
    return `https://www.lorri.ai/${qs ? `?${qs}` : ''}#schedule-form`
  }

  return (
    <>
      <div className={styles.wrap}>
        <div className={styles.backRow}>
          <button className={styles.backBtn} onClick={onBack || (() => history.goBack())}>
            <HiArrowLeft size={16} />
            <span>Back to LoRRI Benchmark</span>
          </button>
        </div>

        {/* ── Hero ── */}
        <div className={styles.hero}>
          {/* heroBadge */}
          {/* <div className={styles.heroBadge}>
            {creditsExhausted ? `YOUR ${lanesCount ?? 2} FREE LANES ARE DONE` : 'EXPLORE UPGRADE OPTIONS'}
          </div> */}

          <h1 className={styles.heroHeading}>
            Now let's scale the value,{' '}
            <span className={styles.heroHeadingAccent}>Subscribe to Mega RFQ</span>
          </h1>
          <p className={styles.heroSub}>
            Keep benchmarking on demand, or join an industry-wide RFQ to negotiate sharper rates with the rest of the market.
          </p>

          {/* <div className={styles.statsStrip}>
            <div className={styles.stripStat}>
              <span className={styles.stripStatValue}>₹200+ Cr</span>
              <span>saved by LoRRI customers</span>
            </div>
            <div className={styles.stripDot} />
            <div className={styles.stripStat}>
              <span className={styles.stripStatValue}>12-18%</span>
              <span>avg Mega RFQ savings</span>
            </div>
            <div className={styles.stripDot} />
            <div className={styles.stripStat}>
              <span className={styles.stripStatValue}>2,300+</span>
              <span>Transporters in the network</span>
            </div>
          </div> */}
        </div>

        {/* ── Two cards ── */}
        <div className={styles.cardGrid}>
          {/* Subscribe card */}
          <div className={`${styles.card} ${styles.cardRecommended}`}>
            <div className={styles.recommendedBadge}>Recommended for self-serve teams</div>

            <div className={styles.cardHeadingWrapper}>
              <div className={styles.cardIconWrap}>
                <HiOutlineStar className={styles.cardIcon} />
              </div>
              <div className={styles.cardHeaderRight}>
                <h2 className={styles.cardHeading}>Subscribe to LoRRI Benchmark</h2>
                <p className={styles.cardDesc}>
                  Unlock 3–5% cost savings through price and transporter discovery.
                </p>
              </div>
            </div>

            <ul className={styles.bulletList}>
              {SUBSCRIBE_BULLETS.map((b, i) => (
                <li key={i} className={styles.bulletItem}>
                  <span className={styles.bulletIcon}><HiCheck /></span>
                  <span>
                    <span className={styles.bulletBold}>{b.bold}</span>{b.rest}
                  </span>
                </li>
              ))}
            </ul>

            <div className={styles.cardCta}>
              <button className={styles.btnGreen} onClick={handleSubscribeClick}>
                Continue with LoRRI Benchmark <HiArrowRight />
              </button>
              <div className={styles.cardCtaNote}>
                <HiLockClosed />
                Secure payment via <strong>&nbsp;Razorpay&nbsp;</strong> · UPI, Card, Netbanking &amp; Wallets
              </div>
            </div>
          </div>

          {/* Mega RFQ card */}
          <div className={styles.card}>
            <div className={styles.cardHeadingWrapper}>
              <div className={`${styles.cardIconWrap} ${styles.cardIconWrapGray}`}>
                <HiOutlineTruck className={`${styles.cardIcon} ${styles.cardIconGray}`} />
              </div>
              <div className={styles.cardHeaderRight}>
                <h2 className={styles.cardHeading}>Join Mega RFQ</h2>
                <p className={styles.cardDesc}>
                  Unlock bigger savings together.
                </p>
              </div>
            </div>

            <ul className={styles.bulletList}>
              {RFQ_BULLETS.map((b, i) => (
                <li key={i} className={styles.bulletItem}>
                  <span className={`${styles.bulletIcon} ${styles.bulletIconBlue}`}><HiCheck /></span>
                  <span>
                    <span className={styles.bulletBold}>{b.bold}</span>{b.rest}
                  </span>
                </li>
              ))}
            </ul>

            <div className={styles.cardCta}>
              <button className={styles.btnPurple} onClick={() => setShowExpressModal(true)}>
                Get your Savings Potential <HiArrowRight />
              </button>
              <div className={styles.cardCtaNote}>
                <HiInformationCircle />
                Our team will scope lane count, volume &amp; window before sharing the event plan.
              </div>
            </div>
          </div>
        </div>

        {/* ── Not sure row ── */}
        <div className={styles.notSureRow}>
          <span className={styles.notSureIcon}><HiCalendarDays /></span>
          <div className={styles.notSureText}>
            <div className={styles.notSureTitle}>Not sure which path is right?</div>
            <div className={styles.notSureSub}>
              We'll review your <a href={buildScheduleFormUrl()} target="_blank" rel="noopener noreferrer">benchmark</a>, savings potential, and recommend Subscribe vs <a href="#!">Mega RFQ</a>.
            </div>
          </div>
          <a
            href={buildScheduleFormUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.btnRecommend}
          >
            Get a recommendation <HiArrowRight />
          </a>
        </div>

        {/* ── Bottom stats ── */}
        {/* <div className={styles.bottomStats}>
          <div className={styles.bottomStatCard}>
            <div className={styles.bottomStatValue}>₹200+ Cr</div>
            <div className={styles.bottomStatLabel}>Saved by LoRRI customers</div>
          </div>
          <div className={styles.bottomStatCard}>
            <div className={styles.bottomStatValue}>12-18%</div>
            <div className={styles.bottomStatLabel}>Avg savings per lane on Mega RFQ</div>
          </div>
          <div className={styles.bottomStatCard}>
            <div className={styles.bottomStatValue}>2,300+</div>
            <div className={styles.bottomStatLabel}>Transporters in the network</div>
          </div>
          <div className={styles.bottomStatCard}>
            <div className={styles.bottomStatValue}>98%</div>
            <div className={styles.bottomStatLabel}>Renewed at year-end</div>
          </div>
        </div> */}
      </div>

      {/* ── Floating Stats Widget ── */}
      {showWidget && !widgetDismissed && (
        <div className={styles.floatingWidget}>
          <div className={styles.floatingWidgetHeader}>
            <span className={styles.floatingWidgetTitle}>LoRRI in numbers</span>
            <button className={styles.floatingWidgetClose} onClick={handleDismissWidget} aria-label="Close">
              <HiXMark />
            </button>
          </div>
          <div className={styles.floatingWidgetGrid}>
            <div className={styles.floatingStat}>
              <div className={styles.floatingStatValue}>₹200+ Cr</div>
              <div className={styles.floatingStatLabel}>Saved by LoRRI customers</div>
            </div>
            <div className={styles.floatingStat}>
              <div className={styles.floatingStatValue}>3–5%</div>
              <div className={styles.floatingStatLabel}>Avg savings per lane on Mega RFQ</div>
            </div>
            <div className={styles.floatingStat}>
              <div className={styles.floatingStatValue}>{LORRI_NUMBERS.NUMBER_OF_LSP}+</div>
              <div className={styles.floatingStatLabel}>Transporters in the network</div>
            </div>
            {/* <div className={styles.floatingStat}>
              <div className={styles.floatingStatValue}>98%</div>
              <div className={styles.floatingStatLabel}>Renewed at year-end</div>
            </div> */}
          </div>
        </div>
      )}

      {/* ── Express Interest Modal ── */}
      {showExpressModal && (
        <div className={styles.modalOverlay} onClick={handleCloseExpressModal}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <div className={styles.modalIconWrap}>
                  <FaTruck className={styles.modalIcon} />
                </div>
                <div>
                  <h2 className={styles.modalTitle}>Express Interest — Mega RFQ</h2>
                  <p className={styles.modalSubtitle}>
                    Tell us when you typically procure freight. We'll loop you into the next industry-wide event.
                  </p>
                </div>
              </div>
              <button className={styles.modalClose} onClick={handleCloseExpressModal} aria-label="Close">
                <HiXMark />
              </button>
            </div>

            {!submitted ? (
              <div className={styles.modalBody}>
                {/* Procurement Window */}
                <div className={styles.modalSection}>
                  <div className={styles.modalSectionLabel}>Your Procurement Window</div>
                  <div className={styles.windowGrid}>
                    <label className={`${styles.windowOption} ${procurementWindow === 'monsoon' ? styles.windowOptionSelected : ''}`}>
                      <input
                        type="radio"
                        name="procWindow"
                        value="monsoon"
                        checked={procurementWindow === 'monsoon'}
                        onChange={() => setProcurementWindow('monsoon')}
                        className={styles.windowRadio}
                      />
                      <div className={styles.windowOptionContent}>
                        <div className={styles.windowCycle}>
                          <HiCalendarDays className={styles.windowCycleIcon} />
                          MONSOON CYCLE
                        </div>
                        <div className={styles.windowMonth}>July / August</div>
                        <div className={styles.windowDesc}>Ahead of festive demand</div>
                      </div>
                    </label>

                    <label className={`${styles.windowOption} ${procurementWindow === 'annual' ? styles.windowOptionSelected : ''}`}>
                      <input
                        type="radio"
                        name="procWindow"
                        value="annual"
                        checked={procurementWindow === 'annual'}
                        onChange={() => setProcurementWindow('annual')}
                        className={styles.windowRadio}
                      />
                      <div className={styles.windowOptionContent}>
                        <div className={styles.windowCycle}>
                          <HiCalendarDays className={styles.windowCycleIcon} />
                          ANNUAL CYCLE
                        </div>
                        <div className={styles.windowMonth}>December / January</div>
                        <div className={styles.windowDesc}>Year-end / new fiscal</div>
                      </div>
                    </label>
                  </div>

                  <label className={`${styles.windowOptionWide} ${procurementWindow === 'other' ? styles.windowOptionSelected : ''}`}>
                    <input
                      type="radio"
                      name="procWindow"
                      value="other"
                      checked={procurementWindow === 'other'}
                      onChange={() => setProcurementWindow('other')}
                      className={styles.windowRadio}
                    />
                    <div className={styles.windowOptionContent}>
                      <span className={styles.windowMonth}>Other / Not sure yet</span>
                      <span className={styles.windowDescInline}>&nbsp;We'll align timing on the discovery call.</span>
                    </div>
                  </label>
                </div>

                {/* Lane Count */}
                <div className={styles.modalSection}>
                  <div className={styles.modalFieldLabel}>
                    Approximate Lane Count
                    <span className={styles.optionalTag}>OPTIONAL</span>
                  </div>
                  <div className={styles.inputWithIcon}>
                    <span className={styles.inputIcon}>#</span>
                    <input
                      type="number"
                      placeholder="e.g. 120"
                      value={laneCount}
                      onChange={e => setLaneCount(e.target.value)}
                      className={styles.modalInput}
                    />
                  </div>
                  <div className={styles.inputHint}>Count each origin–destination–truck-type combination as one lane.</div>
                </div>

                {/* Notes */}
                <div className={styles.modalSection}>
                  <div className={styles.modalFieldLabel}>
                    Anything we should know?
                    <span className={styles.optionalTag}>OPTIONAL</span>
                  </div>
                  <textarea
                    placeholder="Special lanes, modes, commodities, etc."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    className={styles.modalTextarea}
                    rows={3}
                  />
                </div>

                {/* Submitting for */}
                {/* <div className={styles.submittingRow}>
                  <label className={styles.submittingLabel}>
                    <input
                      type="checkbox"
                      checked={submittingForSelf}
                      onChange={e => setSubmittingForSelf(e.target.checked)}
                      className={styles.submittingCheck}
                    />
                    <span>Submitting for:</span>
                    <span className={styles.submittingOrg}>my organisation</span>
                  </label>
                </div> */}

                {/* Footer */}
                <div className={styles.modalFooter}>
                  <button className={styles.btnCancel} onClick={handleCloseExpressModal}>Cancel</button>
                  <button
                    className={styles.btnSubmitInterest}
                    onClick={handleExpressSubmit}
                    disabled={!procurementWindow || submittingInterest}
                  >
                    {submittingInterest ? 'Submitting...' : 'Submit Interest'} <HiArrowRight />
                  </button>
                </div>
              </div>
            ) : (
              /* Success state */
              <div className={styles.modalSuccess}>
                <div className={styles.successIcon}><HiCheck /></div>
                <h3 className={styles.successTitle}>Interest Registered!</h3>
                <p className={styles.successSub}>
                  Our team will reach out to scope the event details and share the next Mega RFQ plan with you.
                </p>
                <button className={styles.btnGreen} onClick={handleCloseExpressModal}>Done</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Token Amount Modal ── */}
      {showAmountModal && (
        <div className={styles.amountModalOverlay} onClick={() => setShowAmountModal(false)}>
          <div className={styles.amountModalBox} onClick={e => e.stopPropagation()}>
            <h3 className={styles.amountModalTitle}>Confirm Payment</h3>
            <p className={styles.amountModalDesc}>
              Enter a amount to confirm your subscription. You may choose any amount that works for you.
            </p>
            <div className={styles.amountInputWrap}>
              <span className={styles.amountCurrencySymbol}>₹</span>
              <input
                className={styles.amountInput}
                type="number"
                min="1"
                value={tokenAmount}
                onChange={e => setTokenAmount(e.target.value)}
                autoFocus
              />
            </div>
            <div className={styles.amountModalActions}>
              <button className={styles.amountCancelBtn} onClick={() => setShowAmountModal(false)}>
                Cancel
              </button>
              <button
                className={styles.amountPayBtn}
                onClick={handleAmountPay}
                disabled={!tokenAmount || Number(tokenAmount) < 1}
              >
                Pay ₹{tokenAmount || 0} <HiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}